/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore, serviceTypes } from '@opengeoweb/store';
import {
  layerActions,
  layerSelectors,
  serviceActions,
  layerTypes,
  layerUtils,
  serviceSelectors,
} from '@opengeoweb/store';
import {
  LayerType,
  queryWMSLayers,
  queryWMSServiceInfo,
} from '@opengeoweb/webmap';
import {
  defaultEdrStyles,
  useEDRGetCollectionDetails,
  useStacCollection,
  getStacCollectionStyles,
  resolvePreferredStyleName,
} from '@opengeoweb/webmap-react';
import RenderEDRLayers from './RenderEDRLayers';
import RenderLayerName from './RenderLayerName';
import RenderLayers from './RenderLayers';

const formatParameterTitle = (
  baseTitle: string,
  parameterKey: string,
): string => {
  const title = `${baseTitle} (${parameterKey}`;
  return title.length > 100 ? `${title.substring(0, 100)}...)` : `${title})`;
};

const getSTACCollectionUrl = (
  serviceRootUrl: string,
  collectionName: string,
): string => {
  const normalizedRoot = serviceRootUrl.replace(/\/$/, '');
  // Only encode if not already encoded to avoid double-encoding
  const encodedName = collectionName.includes('%')
    ? collectionName
    : encodeURIComponent(collectionName);
  return `${normalizedRoot}/collections/${encodedName}`;
};

interface RenderLayersProps {
  layerId: string;
  mapId: string;
  tooltipPrefix?: string;
  skipLocalStyling?: boolean;
}

const RenderLayersConnect: React.FC<RenderLayersProps> = ({
  layerId,
  mapId,
  tooltipPrefix,
  skipLocalStyling,
}: RenderLayersProps) => {
  const dispatch = useDispatch();
  const layerName = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerName(store, layerId),
  );
  const layerDisplayName = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerDisplayName(store, layerId),
  );
  const layerType = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerType(store, layerId),
  );
  const isEdrLayer = layerType === LayerType.edrFeatureLayer;
  const isFeatureLayer = layerType === LayerType.featureLayer;
  const isStacGeoTIFFLayer = layerType === LayerType.stacGeoTIFFLayer;
  const serviceUrl = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerService(store, layerId),
  );
  const resolvedServiceUrl = isStacGeoTIFFLayer
    ? layerUtils.getSTACServiceRootUrl(serviceUrl)
    : serviceUrl;
  const isLayerEnabled = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerEnabled(store, layerId),
  );
  const currentLayerStyle = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerStyle(store, layerId),
  );

  const service = useSelector((store: CoreAppStore) => {
    if (!isStacGeoTIFFLayer) {
      return serviceSelectors.getServiceByUrl(store, serviceUrl);
    }

    return (
      serviceSelectors.getServiceByUrl(store, resolvedServiceUrl) ||
      serviceSelectors.getServiceByUrl(
        store,
        layerUtils.getTrailingSlashVariant(resolvedServiceUrl),
      )
    );
  });

  const layerOptions = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerOptions(store, layerId),
  );

  const layerChangeStyle = React.useCallback(
    (style: string) =>
      dispatch(
        layerActions.layerChangeStyle({
          layerId,
          style,
          mapId,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [layerId, dispatch, mapId],
  );

  const serviceSetLayers = React.useCallback(
    (payload: serviceTypes.SetLayersForServicePayload): void => {
      dispatch(serviceActions.serviceSetLayers(payload));
    },
    [dispatch],
  );

  const addWmsService = async (serviceUrl: string): Promise<void> => {
    try {
      const layers = await queryWMSLayers(serviceUrl);
      const wmService = await queryWMSServiceInfo(serviceUrl);
      serviceSetLayers({
        id: wmService.id!,
        name: wmService.title,
        serviceUrl,
        layers,
        scope: 'user',
        abstract: wmService.abstract,
      });
    } catch (error) {
      console.warn(
        error instanceof Error ? error.message : 'addService failed',
      );
    }
  };

  if (!service && !isEdrLayer && !isFeatureLayer && !isStacGeoTIFFLayer) {
    void addWmsService(serviceUrl);
  }

  const { data } = useEDRGetCollectionDetails(serviceUrl, '', isEdrLayer);

  const { data: stacCollectionData, isFetched: isStacCollectionFetched } =
    useStacCollection(serviceUrl, '', isStacGeoTIFFLayer);

  React.useEffect(() => {
    if (!isStacGeoTIFFLayer || !isStacCollectionFetched) {
      return;
    }

    const styleNames = getStacCollectionStyles(
      stacCollectionData?.renders,
      stacCollectionData?.item_assets,
    ).map(({ name }) => name);
    const nextStyle = resolvePreferredStyleName(styleNames, currentLayerStyle);

    if (currentLayerStyle !== nextStyle) {
      layerChangeStyle(nextStyle);
    }
  }, [
    stacCollectionData,
    isStacCollectionFetched,
    isStacGeoTIFFLayer,
    currentLayerStyle,
    layerChangeStyle,
  ]);

  React.useEffect(() => {
    if (!isStacGeoTIFFLayer || !stacCollectionData || !layerName) {
      return;
    }

    const styles = getStacCollectionStyles(
      stacCollectionData.renders,
      stacCollectionData.item_assets,
    );
    if (styles.length === 0) {
      return;
    }

    serviceSetLayers({
      id: stacCollectionData.id,
      name: stacCollectionData.description || stacCollectionData.id,
      serviceUrl,
      layers: [{ name: layerName, leaf: true, path: [], styles }],
      scope: 'system',
      isUpdating: true,
      type: 'STAC',
    });
  }, [
    stacCollectionData,
    isStacGeoTIFFLayer,
    layerName,
    serviceUrl,
    serviceSetLayers,
  ]);

  React.useEffect(() => {
    if (!data?.parameter_names || service) {
      return;
    }
    const layers = Object.entries(data?.parameter_names)
      .map((parameter) => {
        const parameterTitle =
          parameter[1].label ?? parameter[1].description ?? parameter[0];
        const title = formatParameterTitle(parameterTitle, parameter[0]);

        return {
          name: parameter[0],
          title,
          styles: defaultEdrStyles,
          leaf: true,
          path: [],
        };
      })
      .sort((paramA, paramB) =>
        (paramA.title ?? '').localeCompare(paramB.title ?? ''),
      );

    serviceSetLayers({
      id: data.id,
      name: data.title || 'EDR collection',
      serviceUrl,
      abstract: data.description,
      layers,
      scope: 'system',
      type: 'EDR',
    });
  }, [data, serviceUrl, service, serviceSetLayers]);

  const layers = service?.layers ?? [];

  const layerChangeName = React.useCallback(
    ({ layerId, name }: layerTypes.SetLayerNamePayload) =>
      dispatch(
        layerActions.layerChangeName({
          layerId,
          name,
          mapId,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [dispatch, mapId],
  );

  const layerChangeOptions = React.useCallback(
    ({ layerId, options }: layerTypes.SetLayerOptionsPayload) =>
      dispatch(
        layerActions.layerChangeOptions({
          layerId,
          options,
          mapId,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [dispatch, mapId],
  );

  const layerChangeService = React.useCallback(
    ({ layerId, service }: layerTypes.SetLayerServicePayload) =>
      dispatch(
        layerActions.layerChangeService({
          layerId,
          service,
          mapId,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [dispatch, mapId],
  );

  if (layerType === LayerType.edrFeatureLayer) {
    const edrLayerOptions =
      layerOptions && 'parameterNames' in layerOptions
        ? layerOptions
        : undefined;
    const selectedParams = edrLayerOptions?.parameterNames ?? [];
    const collectionId = edrLayerOptions?.collectionId || '';
    return (
      <RenderEDRLayers
        collectionId={collectionId}
        isEnabled={isLayerEnabled}
        layerName={layerDisplayName}
        layers={layers}
        onChangeLayerOptions={(options): void => {
          layerChangeOptions({ layerId, options });
        }}
        selectedParams={selectedParams}
        skipLocalStyling={skipLocalStyling}
        tooltipPrefix={tooltipPrefix}
      />
    );
  }

  if (layerType === LayerType.featureLayer) {
    return <RenderLayerName layerName={layerDisplayName} />;
  }

  return (
    <RenderLayers
      isEnabled={isLayerEnabled}
      layerName={layerName}
      layers={layers}
      onChangeLayerName={(name): void => {
        layerChangeName({ layerId, name });

        const selectedLayer = layers.find((layer) => layer.name === name);
        const selectedLayerStyleNames = (selectedLayer?.styles ?? [])
          .map((style) => style.name)
          .filter((styleName): styleName is string => !!styleName);

        const nextStyle = resolvePreferredStyleName(
          selectedLayerStyleNames,
          currentLayerStyle,
        );

        if (currentLayerStyle !== nextStyle) {
          layerChangeStyle(nextStyle);
        }

        if (isStacGeoTIFFLayer) {
          layerChangeService({
            layerId,
            service: getSTACCollectionUrl(resolvedServiceUrl, name),
          });
        }
      }}
      skipLocalStyling={skipLocalStyling}
      tooltipPrefix={tooltipPrefix}
    />
  );
};

export default RenderLayersConnect;
